/**
 * The furniture of a section — `docs/new-request.md` §5.0, Board 2.
 *
 * A section is **open** (its heading and fields), **waiting** (a white,
 * outlined one-line row that a click opens — never greyed, shut or
 * disabled) or **folded** (one line: green when ready, amber when started
 * and left). The section bar's chips say the same three things with a ring,
 * an amber ring and dot, or a green tick.
 */
import type { ComponentType, ReactNode } from 'react'
import { cn } from '../lib/cn.js'
import {
  IconCalendar, IconChevronDown, IconLayers, IconPerson, IconPin, type IconProps,
} from '../frame/icons.js'
import { SECTION_LABELS, SECTION_SUBS, statusOf, type SectionKey, type SectionStatus } from './model.js'
import { CheckGlyph } from '../components/keycap.js'
import { useRequestSheet, useRequestSheetMaybe } from './sheet-context.js'

export const SECTION_ICONS: Record<SectionKey, ComponentType<IconProps>> = {
  contact: IconPerson,
  property: IconPin,
  job: IconLayers,
  appointment: IconCalendar,
}

/** The 22px circle in front of a heading or a row. */
export function SectionMark({ section, look }: {
  section: SectionKey
  /** `outline` waiting, `tint` open, `ready` a solid green tick. */
  look: 'outline' | 'tint' | 'ready'
}) {
  const Icon = SECTION_ICONS[section]
  if (look === 'ready') {
    return (
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent text-surface">
        <CheckGlyph size={12} stroke={3} />
      </span>
    )
  }
  return (
    <span className={cn(
      'inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px]',
      look === 'tint'
        ? 'border-accent-line bg-accent-soft text-accent-dark'
        : 'border-line-strong bg-surface text-text-muted',
    )}>
      <Icon size={12} />
    </span>
  )
}

/** An open section's heading: the mark, the name at 14.5/700 and its sub-line. */
export function SectionHeading({ section, ready, sub, right }: {
  section: SectionKey
  ready: boolean
  sub?: ReactNode
  right?: ReactNode
}) {
  /* A phone's heading is 15px with an 8px gap and no sub-line room to spare (Board 14). */
  const api = useRequestSheetMaybe()
  const phone = api?.phone ?? false
  /*
   * The section you are in is tinted, empty or not (Boards 2, 3); one that is
   * open further down with nothing in it yet is the outline it was (Boards 5, 6).
   */
  const empty = api ? statusOf(api.state, section) === 'empty' && api.current !== section : false
  return (
    <div className={cn('flex items-center', phone ? 'mb-2.5 gap-2' : 'mb-3 gap-2.5')} id={`request-${section}`}>
      <SectionMark section={section} look={ready ? 'ready' : empty ? 'outline' : 'tint'} />
      <b className={cn('font-bold text-text', phone ? 'text-[15px]' : 'text-[14.5px]')}>{SECTION_LABELS[section]}</b>
      {sub !== undefined && <span className="min-w-0 truncate text-[12px] text-text-muted">{sub}</span>}
      <span className="flex-1" />
      {right}
    </div>
  )
}

/** A waiting section: one white row, the whole of it a button (§5.0). */
export function WaitingRow({ section, next, onOpen }: {
  section: SectionKey
  next: boolean
  onOpen: () => void
}) {
  return (
    <button
      type="button"
      id={`request-${section}`}
      onClick={onOpen}
      data-waiting={section}
      className={cn('flex w-full items-center gap-2.5 rounded-[12px] border border-line bg-surface',
        'px-3.5 py-3 text-left hover:border-line-strong')}
    >
      <SectionMark section={section} look="outline" />
      <b className="text-[14px] font-bold text-text">{SECTION_LABELS[section]}</b>
      <span className="min-w-0 truncate text-[12px] text-text-muted">{SECTION_SUBS[section]}</span>
      <span className="flex-1" />
      {next && <span className="text-[11.5px] text-text-faint">next</span>}
      <span className="inline-flex text-text-muted"><IconChevronDown size={16} /></span>
    </button>
  )
}

/**
 * A folded section, one line: green with a tick when ready, amber with the
 * started mark when something was typed and left (Board 2 D, Board 7).
 */
export function FoldedLine({ section, status, title, summary, onOpen }: {
  section: SectionKey
  status: SectionStatus
  title: string
  summary: string
  /** Absent in book mode, where Contact, Property and Job can't be changed (§11.4). */
  onOpen?: () => void
}) {
  const started = status !== 'ready'
  return (
    <div
      id={`request-${section}`}
      data-folded={section}
      className={cn('flex items-center gap-2.5 rounded-[10px] border px-3 py-[9px]',
        started ? 'border-amber-band-line bg-amber-soft' : 'border-line-faint bg-surface-alt')}
    >
      {started
        ? (
          <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-2 border-amber-mark bg-surface">
            <i className="block h-[7px] w-[7px] rounded-full bg-amber-mark" />
          </span>
        )
        : <SectionMark section={section} look="ready" />}
      <b className="whitespace-nowrap text-[13px] font-bold text-text">{title}</b>
      <span className="min-w-0 flex-1 truncate text-[12.5px] text-text-muted">{summary}</span>
      {onOpen && (
        <button type="button" onClick={onOpen}
          className="h-6 shrink-0 rounded-button px-1.5 text-[12px] font-semibold text-accent hover:underline">
          Open
        </button>
      )}
    </div>
  )
}

/** The bar's status marks (Board 2 D legend). */
export function ChipMark({ status, small }: { status: SectionStatus; small?: boolean }) {
  if (status === 'ready') {
    return (
      <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full bg-accent text-surface',
        small ? 'h-[14px] w-[14px]' : 'h-[15px] w-[15px]')}>
        <CheckGlyph size={8} stroke={3} />
      </span>
    )
  }
  if (status === 'started') {
    return (
      <span className={cn('inline-flex shrink-0 items-center justify-center rounded-full border-2 border-amber-mark bg-surface',
        small ? 'h-[12px] w-[12px]' : 'h-[13px] w-[13px]')}>
        <i className="block h-[4px] w-[4px] rounded-full bg-amber-mark" />
      </span>
    )
  }
  return <i className={cn('block shrink-0 rounded-full border-2 border-line-strong bg-surface',
    small ? 'h-[12px] w-[12px]' : 'h-[13px] w-[13px]')} />
}

/**
 * The section bar: four white chips and *<n> of 4 ready* (§5.0). The chip of
 * the section you are in is the app's selected chip — never black (Aaron,
 * review 2).
 */
export function SectionBar({ statuses, current, ready, onChoose, phone }: {
  statuses: Record<SectionKey, SectionStatus>
  current: SectionKey | null
  ready: number
  onChoose: (section: SectionKey) => void
  phone?: boolean
}) {
  return (
    <div
      role="tablist"
      aria-label="Sections"
      className={cn('flex items-center', phone ? 'no-scrollbar gap-[5px] overflow-x-auto' : 'gap-1.5')}
    >
      {(['contact', 'property', 'job', 'appointment'] as const).map((section) => (
        <button
          key={section}
          type="button"
          role="tab"
          aria-selected={current === section}
          data-chip={section}
          data-status={statuses[section]}
          onClick={() => onChoose(section)}
          className={cn(
            'inline-flex h-[30px] shrink-0 items-center whitespace-nowrap rounded-chip border',
            'text-[12.5px] font-semibold',
            phone ? 'gap-[5px] pl-2 pr-2.5' : 'gap-[7px] pl-[9px] pr-3',
            current === section
              ? 'border-accent-line bg-accent-soft text-accent-dark'
              : 'border-line bg-surface text-text',
          )}
        >
          <ChipMark status={statuses[section]} small={phone} />
          {SECTION_LABELS[section]}
        </button>
      ))}
      {!phone && (
        <>
          <span className="flex-1" />
          <span className="whitespace-nowrap text-[11.5px] text-text-faint">{ready} of 4 ready</span>
        </>
      )}
    </div>
  )
}

/**
 * *Replace what's in Contact with Dana Okafor?* **Replace · Keep mine** — a
 * later choice never overwrites typed fields without asking (§5.4, §19 B2).
 * A section draws it right under its heading; it is absent otherwise.
 */
export function ReplaceAsk({ section }: { section: 'contact' | 'property' }) {
  const { replaceAsk, answerReplace } = useRequestSheet()
  if (!replaceAsk || replaceAsk.section !== section) return null
  return (
    <div role="alert" className={cn('mb-3 flex items-center gap-2.5 rounded-card border',
      'border-amber-band-line bg-amber-soft px-3 py-2 text-[12.5px] text-text')}>
      <span className="min-w-0 flex-1">
        Replace what’s in {SECTION_LABELS[section]} with <b>{replaceAsk.name}</b>?
      </span>
      <button type="button" onClick={() => answerReplace(true)}
        className="h-7 rounded-button bg-accent px-2.5 text-[12px] font-semibold text-surface">
        Replace
      </button>
      <button type="button" onClick={() => answerReplace(false)}
        className="h-7 rounded-button border border-line bg-surface px-2.5 text-[12px] font-semibold text-text">
        Keep mine
      </button>
    </div>
  )
}

/** The red line under a field after Create was pressed (§11.3, Board 9 A). */
export function MissingHint({ text }: { text: string | null }) {
  if (!text) return null
  return <span role="alert" className="mt-1 block text-[11px] leading-[1.4] text-danger">{text}</span>
}
